import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import TiltCard from "@/components/TiltCard";
import { Info, ArrowUp, ArrowDown } from "lucide-react";

function useCountUp(target, duration = 1400) {
    const [val, setVal] = useState(0);
    const raf = useRef(null);
    useEffect(() => {
        if (target == null || isNaN(target)) {
            setVal(0);
            return;
        }
        const start = performance.now();
        const from = 0;
        const tick = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(from + (target - from) * eased);
            if (p < 1) raf.current = requestAnimationFrame(tick);
        };
        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [target, duration]);
    return val;
}

function fmtNumber(n) {
    return new Intl.NumberFormat("en-US").format(Math.round(n));
}

export default function MetricCard({
    label,
    value,
    display,
    suffix = "",
    delta,
    deltaSuffix = "",
    tooltip,
    testid,
    index = 0,
    isNumeric = true,
}) {
    const [showTip, setShowTip] = useState(false);
    const animated = useCountUp(isNumeric ? Number(value) || 0 : 0);
    const shown = isNumeric ? `${fmtNumber(animated)}${suffix}` : display ?? value;

    const deltaNum = typeof delta === "number" ? delta : null;
    const up = deltaNum != null && deltaNum >= 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
        >
            <TiltCard intensity={6} lift={8}>
                <div
                    data-testid={testid}
                    className="relative h-full rounded-2xl surface-3d p-6 md:p-7 overflow-hidden"
                >
                    <div className="flex items-start justify-between gap-3 mb-4">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--accent-champagne)]/75">
                            {label}
                        </p>
                        <button
                            type="button"
                            onMouseEnter={() => setShowTip(true)}
                            onMouseLeave={() => setShowTip(false)}
                            onClick={() => setShowTip((v) => !v)}
                            className="shrink-0 text-[var(--accent-gold)]/70 hover:text-[var(--accent-gold)] transition-colors"
                            aria-label={`What is ${label}?`}
                            data-testid={testid ? `${testid}-info` : undefined}
                        >
                            <Info size={15} />
                        </button>
                    </div>

                    <p className="font-display font-bold text-4xl md:text-5xl leading-none gold-shimmer">
                        {shown}
                    </p>

                    {deltaNum != null && (
                        <div
                            className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                                up
                                    ? "text-emerald-300 bg-emerald-900/30"
                                    : "text-red-300 bg-red-900/30"
                            }`}
                        >
                            {up ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                            {up ? "+" : ""}
                            {fmtNumber(Math.abs(deltaNum))}
                            {deltaSuffix} since last update
                        </div>
                    )}

                    {showTip && tooltip && (
                        <div className="absolute left-4 right-4 bottom-4 z-20 rounded-xl bg-[#1a0000] border border-[var(--border-accent)] p-3 text-xs text-[var(--text-secondary)] leading-relaxed shadow-xl">
                            {tooltip}
                        </div>
                    )}
                </div>
            </TiltCard>
        </motion.div>
    );
}
